import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import QRCode from 'react-qr-code';
import { useAuth } from '../../context/AuthContext';
import UserLayout from '../../components/user/UserLayout';
import toast from 'react-hot-toast';
import { CheckCircle, Upload, Camera, Clock, Shield, Copy, AlertCircle, Bitcoin, Building2, Smartphone } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function SetupFee() {
  const { user, refreshUser } = useAuth();
  const [paymentConfig, setPaymentConfig] = useState(null);
  const [screenshot, setScreenshot] = useState(null);
  const [screenshotPreview, setScreenshotPreview] = useState(null);
  const [transactionId, setTransactionId] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState('upi');
  const fileRef = useRef();
  const navigate = useNavigate();

  // INR → USD conversion rate (from admin config, default 83)
  const usdRate = paymentConfig?.usdRate || 83;
  const SETUP_FEE_USD = parseInt(process.env.REACT_APP_SETUP_FEE) || 150;
  const SETUP_FEE_INR = Math.round(SETUP_FEE_USD * usdRate);

  useEffect(() => {
    axios.get('/api/payment/admin-payment-config').then(r => {
      setPaymentConfig(r.data);
      // Auto-select first available method
      if (r.data.trc20Enabled && r.data.trc20Address) setSelectedMethod('trc20');
      else if (r.data.bep20Enabled && r.data.bep20Address) setSelectedMethod('bep20');
      else if (r.data.erc20Enabled && r.data.erc20Address) setSelectedMethod('erc20');
      else if (r.data.upiId) setSelectedMethod('upi');
      else if (r.data.bankName) setSelectedMethod('bank');
    }).catch(() => {});
  }, []);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error('File size must be under 5MB');
    const reader = new FileReader();
    reader.onloadend = () => {
      setScreenshot(reader.result);
      setScreenshotPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async () => {
    if (!screenshot) return toast.error('Please upload payment screenshot first');
    if (!transactionId || !transactionId.trim()) return toast.error('Please enter transaction ID');
    setLoading(true);
    try {
      await axios.post('/api/payment/submit-setup-fee', {
        screenshot,
        paymentMethod: selectedMethod,
        transactionId: transactionId.trim(),
      });
      await refreshUser();
      toast.success('Payment screenshot submitted! Waiting for admin approval.');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to submit');
    } finally {
      setLoading(false);
    }
  };

  const handlePayFromWallet = async () => {
    if ((user?.mainWalletBalance || 0) < SETUP_FEE_USD) {
      return toast.error('Insufficient main wallet balance');
    }
    setLoading(true);
    try {
      await axios.post('/api/payment/submit-setup-fee', {
        paymentMethod: 'mainWallet'
      });
      await refreshUser();
      toast.success('Setup fee paid from main wallet. Account activated!');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to pay from main wallet');
    } finally {
      setLoading(false);
    }
  };

  const copyText = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied!`);
  };

  const getCryptoQrValue = (address, method) => {
    if (!address) return '';
    switch (method) {
      case 'trc20':
        return `tron:${address}`;
      case 'bep20':
      case 'erc20':
        return `ethereum:${address}`;
      default:
        return address;
    }
  };

  // ── Already approved ─────────────────────────────────────────────────────
  if (user?.setupFeeApproved) {
    return (
      <UserLayout>
        <div className="max-w-md mx-auto text-center py-20">
          <CheckCircle className="w-16 h-16 text-primary-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">Account Active!</h2>
          <p className="text-gray-400 mb-6">Your subcription fee has been approved.</p>
          <button onClick={() => navigate('/dashboard')}
            className="bg-primary-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-primary-500 transition-colors">
            Go to Dashboard
          </button>
        </div>
      </UserLayout>
    );
  }

  // ── Already submitted ─────────────────────────────────────────────────────
  if (user?.setupFeePaid && !user?.setupFeeApproved) {
    return (
      <UserLayout>
        <div className="max-w-md mx-auto text-center py-20">
          <Clock className="w-16 h-16 text-amber-500 mx-auto mb-4 animate-pulse" />
          <h2 className="text-2xl font-bold text-white mb-2">Waiting for Approval</h2>
          <p className="text-gray-400 mb-2">Your payment screenshot has been submitted.</p>
          <p className="text-gray-500 text-sm">Admin will verify and approve shortly.</p>
          <div className="mt-6 bg-dark-700 border border-white/5 rounded-2xl p-4 text-left space-y-2">
            <p className="text-gray-400 text-sm">Submitted: <span className="text-white">
              {user?.setupFeeSubmittedAt ? new Date(user.setupFeeSubmittedAt).toLocaleString() : '—'}
            </span></p>
            {user?.setupFeeNote && (
              <p className="text-gray-400 text-sm mt-1">Method: <span className="text-primary-400 capitalize">{user.setupFeeNote}</span></p>
            )}
            {user?.setupFeeTransactionId && (
              <p className="text-gray-400 text-sm mt-1">Transaction ID: <span className="text-primary-400">{user.setupFeeTransactionId}</span></p>
            )}
          </div>
        </div>
      </UserLayout>
    );
  }

  // ── Available payment methods ──────────────────────────────────────────────
  const cryptoNetworks = [
    { key: 'trc20', label: 'TRC20 (USDT)', sublabel: 'Tron Network', color: 'text-red-400', border: 'border-red-500/30', bg: 'bg-red-500/10', address: paymentConfig?.trc20Address, enabled: paymentConfig?.trc20Enabled },
    { key: 'bep20', label: 'BEP20 (USDT)', sublabel: 'Binance Smart Chain', color: 'text-amber-400', border: 'border-amber-500/30', bg: 'bg-amber-500/10', address: paymentConfig?.bep20Address, enabled: paymentConfig?.bep20Enabled },
    { key: 'erc20', label: 'ERC20 (USDT)', sublabel: 'Ethereum Network', color: 'text-blue-400', border: 'border-blue-500/30', bg: 'bg-blue-500/10', address: paymentConfig?.erc20Address, enabled: paymentConfig?.erc20Enabled },
  ].filter(n => n.enabled && n.address);

  const hasFiat = !!(
    (paymentConfig?.upiId && paymentConfig?.upiEnabled !== false) ||
    (paymentConfig?.bankName && paymentConfig?.bankEnabled !== false)
  );
  const hasCrypto = cryptoNetworks.length > 0;
  const canPayFromWallet = (user?.mainWalletBalance || 0) >= SETUP_FEE_USD;
  const isCryptoMethod = ['trc20', 'bep20', 'erc20'].includes(selectedMethod);

  return (
    <UserLayout>
      <div className="max-w-2xl mx-auto animate-fade-in">
        <h1 className="text-2xl font-bold text-white mb-2">Pay Subcription Fee</h1>
        <p className="text-gray-400 mb-6">
          One-time fee to activate your trading account
        </p>

        {/* Amount Banner */}
        <div className="bg-gradient-to-br from-primary-600/20 to-blue-600/10 border border-primary-500/30 rounded-2xl p-5 mb-6 flex items-center gap-4">
          <Shield className="w-10 h-10 text-primary-400 flex-shrink-0" />
          <div>
            <p className="text-gray-400 text-sm">subcription Fee</p>
            <div className="flex items-baseline gap-3">
              <p className="text-3xl font-bold text-white">${SETUP_FEE_USD}</p>
              {hasFiat && (
                <p className="text-gray-400 text-sm">≈₹{SETUP_FEE_INR.toLocaleString()} <span className="text-gray-600 text-xs">(if paying via UPI/Bank)</span></p>
              )}
            </div>
          </div>
        </div>

        {/* Payment Method Selector */}
        {(hasFiat || hasCrypto) && (
          <div className="bg-dark-700 border border-white/5 rounded-2xl p-5 mb-5">
            <p className="text-gray-400 text-sm mb-3 font-medium">Select Payment Method</p>
            <div className="flex flex-wrap gap-2">
              {paymentConfig?.upiId && paymentConfig?.upiEnabled !== false && (
                <button onClick={() => setSelectedMethod('upi')}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium border transition-all ${selectedMethod === 'upi' ? 'bg-primary-600 border-primary-500 text-white' : 'bg-dark-600 border-white/10 text-gray-400 hover:text-white'}`}>
                  <Smartphone className="w-4 h-4" /> UPI
                  <span className="text-xs opacity-70">₹{SETUP_FEE_INR.toLocaleString()}</span>
                </button>
              )}
              {paymentConfig?.bankName && paymentConfig?.bankEnabled !== false && (
                <button onClick={() => setSelectedMethod('bank')}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium border transition-all ${selectedMethod === 'bank' ? 'bg-primary-600 border-primary-500 text-white' : 'bg-dark-600 border-white/10 text-gray-400 hover:text-white'}`}>
                  <Building2 className="w-4 h-4" /> Bank Transfer
                  <span className="text-xs opacity-70">₹{SETUP_FEE_INR.toLocaleString()}</span>
                </button>
              )}
              {cryptoNetworks.map(n => (
                <button key={n.key} onClick={() => setSelectedMethod(n.key)}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium border transition-all ${selectedMethod === n.key ? 'bg-primary-600 border-primary-500 text-white' : 'bg-dark-600 border-white/10 text-gray-400 hover:text-white'}`}>
                  <Bitcoin className="w-4 h-4" /> {n.label}
                  <span className="text-xs opacity-70">${SETUP_FEE_USD}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        <div className={`bg-dark-700 rounded-2xl p-5 mb-6 border ${canPayFromWallet ? 'border-emerald-500/30' : 'border-white/5'}`}>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <p className="text-gray-400 text-sm font-medium">Pay from Main Wallet</p>
              <p className="text-white font-semibold mt-1">${(user?.mainWalletBalance || 0).toFixed(2)} available</p>
            </div>
            <button onClick={handlePayFromWallet}
              disabled={!canPayFromWallet || loading}
              className={`px-4 py-3 rounded-xl text-sm font-semibold transition-colors ${canPayFromWallet ? 'bg-emerald-500 text-black hover:bg-emerald-400' : 'bg-white/10 text-gray-400 cursor-not-allowed'}`}>
              {canPayFromWallet ? `Pay $${SETUP_FEE_USD} from Main Wallet` : 'Insufficient Main Wallet Balance'}
            </button>
          </div>
          <p className="text-gray-500 text-xs mt-3">If you have enough balance in your main wallet, pay the setup fee instantly without uploading a screenshot.</p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Payment Details Panel */}
          <div className="bg-dark-700 border border-white/5 rounded-2xl p-6">
            <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
              <Shield className="w-5 h-5 text-primary-400" />
              Payment Details
            </h3>

            {!paymentConfig && (
              <div className="flex items-center justify-center h-40">
                <div className="w-8 h-8 border-3 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
              </div>
            )}

            {/* UPI Method */}
            {paymentConfig && selectedMethod === 'upi' && paymentConfig.upiId && paymentConfig?.upiEnabled !== false && (
              <div className="space-y-4">
                <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-3 text-center">
                  <p className="text-amber-300 text-sm font-semibold">Send${SETUP_FEE_INR.toLocaleString()}</p>
                  <p className="text-amber-400/70 text-xs mt-0.5">= ${SETUP_FEE_USD} at rate 1$ =₹{usdRate}</p>
                </div>

                {paymentConfig.qrCodeImage && (
                  <div className="text-center">
                    <img src={paymentConfig.qrCodeImage} alt="QR" className="w-40 h-40 mx-auto rounded-xl border border-white/10 bg-white p-1 object-contain" />
                    <p className="text-gray-500 text-xs mt-1">Scan to pay via UPI</p>
                  </div>
                )}

                <div className="bg-dark-600 rounded-xl p-3">
                  <p className="text-gray-500 text-xs mb-1">UPI ID</p>
                  <div className="flex items-center justify-between">
                    <p className="text-white font-mono font-semibold">{paymentConfig.upiId}</p>
                    <button onClick={() => copyText(paymentConfig.upiId, 'UPI ID')}
                      className="text-primary-400 hover:text-primary-300">
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>
                  {paymentConfig.upiName && <p className="text-gray-500 text-xs mt-0.5">{paymentConfig.upiName}</p>}
                </div>

                {paymentConfig.instructions && (
                  <p className="text-gray-500 text-xs leading-relaxed">{paymentConfig.instructions}</p>
                )}
              </div>
            )}

            {/* Bank Method */}
            {paymentConfig && selectedMethod === 'bank' && paymentConfig.bankName && (
              <div className="space-y-4">
                <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-3 text-center">
                  <p className="text-amber-300 text-sm font-semibold">Transfer${SETUP_FEE_INR.toLocaleString()}</p>
                  <p className="text-amber-400/70 text-xs mt-0.5">= ${SETUP_FEE_USD} at rate 1$ =₹{usdRate}</p>
                </div>
                <div className="bg-dark-600 rounded-xl p-4 space-y-2.5 text-sm">
                  {[
                    ['Account Name', paymentConfig.accountHolderName],
                    ['Bank', paymentConfig.bankName],
                    ['Account Number', paymentConfig.accountNumber],
                    ['IFSC Code', paymentConfig.ifscCode],
                  ].map(([k, v]) => v ? (
                    <div key={k} className="flex items-center justify-between">
                      <span className="text-gray-400">{k}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-white font-mono text-xs">{v}</span>
                        <button onClick={() => copyText(v, k)} className="text-gray-600 hover:text-primary-400">
                          <Copy className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  ) : null)}
                </div>
              </div>
            )}

            {/* Crypto Methods (TRC20 / BEP20 / ERC20) */}
            {paymentConfig && isCryptoMethod && (() => {
              const net = cryptoNetworks.find(n => n.key === selectedMethod);
              if (!net) return (
                <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-3">
                  <p className="text-amber-300 text-sm">This network is not configured yet.</p>
                </div>
              );
              return (
                <div className="space-y-4">
                  <div className={`${net.bg} border ${net.border} rounded-xl p-3 text-center`}>
                    <p className={`${net.color} text-sm font-semibold`}>{net.label}</p>
                    <p className="text-gray-300 text-sm mt-0.5">{net.sublabel}</p>
                    <p className="text-white font-bold text-lg mt-1">${SETUP_FEE_USD} USDT</p>
                    <p className="text-gray-500 text-xs mt-0.5">Send exactly ${SETUP_FEE_USD} USDT</p>
                  </div>

                  <div className="text-center">
                    <div className="inline-block rounded-2xl bg-dark-700 p-3 mb-4">
                      <QRCode
                        value={getCryptoQrValue(net.address, net.key)}
                        size={156}
                        bgColor="#111827"
                        fgColor="#ffffff"
                      />
                    </div>
                    <p className="text-gray-500 text-xs">Scan to pay via {net.label.split(' ')[0]}</p>
                  </div>

                  <div className="bg-dark-600 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-gray-400 text-xs">Wallet Address</p>
                      <button onClick={() => copyText(net.address, 'Wallet address')}
                        className="flex items-center gap-1 text-primary-400 hover:text-primary-300 text-xs">
                        <Copy className="w-3 h-3" /> Copy
                      </button>
                    </div>
                    <p className="text-white font-mono text-xs break-all bg-dark-500 rounded-lg p-2 leading-relaxed select-all">
                      {net.address}
                    </p>
                  </div>

                  <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-3 text-xs text-red-300">
                    ⚠️ Send only {net.label.split(' ')[0]} tokens on {net.sublabel}. Wrong network = lost funds!
                  </div>
                </div>
              );
            })()}

            {/* No payment method available */}
            {paymentConfig && !hasFiat && !hasCrypto && (
              <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4 text-center">
                <AlertCircle className="w-8 h-8 text-amber-400 mx-auto mb-2" />
                <p className="text-amber-300 text-sm">Payment details not configured. Contact admin.</p>
              </div>
            )}
          </div>

          {/* Screenshot Upload */}
          <div className="bg-dark-700 border border-white/5 rounded-2xl p-6">
            <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
              <Upload className="w-5 h-5 text-blue-400" />
              Upload Payment Proof
            </h3>

            <div className="space-y-4">
              <div onClick={() => fileRef.current?.click()}
                className="border-2 border-dashed border-white/20 hover:border-primary-500/50 rounded-xl p-6 text-center cursor-pointer transition-colors">
                {screenshotPreview ? (
                  <img src={screenshotPreview} alt="Screenshot" className="max-h-48 mx-auto rounded-lg object-contain" />
                ) : (
                  <>
                    <Camera className="w-10 h-10 text-gray-600 mx-auto mb-2" />
                    <p className="text-gray-400 text-sm">Click to upload payment screenshot</p>
                    <p className="text-gray-600 text-xs mt-1">JPG, PNG up to 5MB</p>
                  </>
                )}
              </div>
              <input ref={fileRef} type="file" accept="image/*" onChange={handleFileChange} className="hidden" />

              {screenshotPreview && (
                <button onClick={() => { setScreenshot(null); setScreenshotPreview(null); }}
                  className="w-full text-gray-400 hover:text-white text-sm border border-white/10 rounded-xl py-2 transition-colors">
                  Remove & Re-upload
                </button>
              )}

              <div>
                <label className="block text-xs text-gray-400 mb-1.5">Transaction ID</label>
                <input value={transactionId} onChange={e => setTransactionId(e.target.value)}
                  className="w-full bg-dark-600 border border-white/10 text-white rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-primary-500"
                  placeholder="Enter payment transaction ID" />
              </div>

              <div className="bg-dark-600 rounded-xl p-3 space-y-1 text-xs text-gray-500">
                {isCryptoMethod ? (
                  <>
                    <p>✓ Send exactly ${SETUP_FEE_USD} USDT to the wallet address</p>
                    <p>✓ Take a screenshot of the transaction confirmation</p>
                  </>
                ) : (
                  <>
                    <p>✓ Send ₹{SETUP_FEE_INR.toLocaleString()} via {selectedMethod === 'upi' ? 'UPI' : 'Bank Transfer'}</p>
                    <p>✓ Take a screenshot of the payment confirmation</p>
                  </>
                )}
                <p>✓ Upload the screenshot here and submit</p>
                <p>✓ Admin will verify and activate your account</p>
              </div>

              <button onClick={handleSubmit} disabled={loading || !screenshot}
                className="w-full bg-primary-600 hover:bg-primary-500 disabled:bg-primary-800 disabled:cursor-not-allowed text-white font-semibold py-3 rounded-xl transition-colors flex items-center justify-center gap-2">
                {loading
                  ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  : <><Upload className="w-4 h-4" /> Submit Payment Proof</>}
              </button>
            </div>
          </div>
        </div>
      </div>
    </UserLayout>
  );
}
